import React, { useState } from "react";

import { LinkWithUTM as Link } from "../components/LinkWithUTM";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import GroupTech from "../components/icons/Approach/GroupTech";
import CalendarDay from "../components/icons/Approach/CalendarDay";
import PieChart from "../components/icons/Approach/PieChart";
import TextDoc from "../components/icons/Approach/TextDoc";
import TrustpilotInline from "../components/TrustpilotInline";
import { MicroCombo } from "../components/Trustpilot/TrustpilotWidgets";

import "../../scss/main.scss";

const imagePath = "/assets/marketing-site/images";

const qualityStandards = [
	{
		number: "01",
		title: "Safer recruitment",
		items: [
			"Enhanced DBS check completed",
			"Three references carefully verified",
			"Rigorous safer recruitment checks",
		],
	},
	{
		number: "02",
		title: "Ready for real life",
		items: [
			"First-class training programme",
			"Excellent spoken and written English",
			"Able to prepare nutritious meals",
			"Infection prevention, including COVID-19 protocols",
		],
	},
	{
		number: "03",
		title: "Supported and accountable",
		items: [
			"24/7 access to our care management team",
			"Public Liability Insurance included",
			"Edyn policies and procedures oversee each placement",
		],
	},
];

const carerProfiles = [
	{
		name: "Olivia",
		image: "olivia.png",
		years: 4,
		languages: "English and French",
		travel: ["Driver", "Own car"],
		conditions: ["Dementia", "Parkinson's", "Companion care"],
		skills: ["Personal care", "Medication", "Home routines"],
		superpower: "Making everyday moments feel calm and familiar",
	},
	{
		name: "Ayaan",
		image: "ayaan.png",
		years: 5,
		languages: "English and Sinhalese",
		travel: ["Driver"],
		conditions: ["Stroke recovery", "Mobility support", "Respite care"],
		skills: ["Companionship", "Meal preparation", "Mobility support"],
		superpower: "Helping people stay connected to the things they enjoy",
	},
	{
		name: "Adela",
		image: "adela.png",
		years: 3,
		languages: "English and Czech",
		travel: ["Driver", "Own car"],
		conditions: ["Dementia", "Multiple sclerosis", "Cancer support"],
		skills: ["Medication", "Staying active", "Personal care"],
		superpower: "Bringing energy, encouragement and thoughtful detail",
	},
];

const technologyViews = [
	{
		title: "Your care team",
		copy:
			"See the professional carers and regional team supporting care, all in one clear place.",
		image: "tech-image-00.png",
		alt: "Edyn care app showing proposed carers and their profile details",
		icon: GroupTech,
	},
	{
		title: "Visit summary",
		copy:
			"Useful updates make it easier to understand what happened during a visit and what comes next.",
		image: "tech-image-01.png",
		alt: "Edyn care app showing a visit summary and carer note",
		icon: CalendarDay,
	},
	{
		title: "Wellness report",
		copy:
			"Simple wellbeing updates help the wider care team notice patterns and respond thoughtfully.",
		image: "tech-image-02.png",
		alt: "Edyn care app showing a wellness report",
		icon: PieChart,
	},
	{
		title: "Regular care statements",
		copy:
			"Clear statements give families a straightforward view of hours, visits and care arrangements.",
		image: "tech-image-03.png",
		alt: "Edyn care app showing a regular care statement",
		icon: TextDoc,
	},
];

function ProfileIcon({ type }) {
	if (type === "experience") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<circle cx="12" cy="12" r="8.25" />
				<path d="M12 7.5v5l3.35 2" />
			</svg>
		);
	}

	if (type === "driver") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path d="M4.5 15.5v2.25h2v-2.25m11 0v2.25h2v-2.25M4 13v2.5h16V13l-1.55-5h-12.9L4 13Z" />
				<path d="M7 12h.01M17 12h.01" />
			</svg>
		);
	}

	if (type === "conditions") {
		return (
			<svg viewBox="0 0 24 24" aria-hidden="true">
				<path d="M12 4.5v15M4.5 12h15" />
				<rect x="4" y="4" width="16" height="16" rx="3" />
			</svg>
		);
	}

	return (
		<svg viewBox="0 0 24 24" aria-hidden="true">
			<path d="M5 6.25h14M5 12h14M5 17.75h9" />
			<path d="m16.4 17.3 1.5 1.5 2.7-3" />
		</svg>
	);
}

const careTeamRoles = [
	{
		number: "01",
		title: "Professional carer",
		copy:
			"One-to-one support in the home, shaped around personal routines, preferences and the agreed care plan.",
	},
	{
		number: "02",
		title: "Regional Care Coordinator",
		copy:
			"Keeps schedules, travel, introductions and practical changes joined up so the placement runs smoothly.",
	},
	{
		number: "03",
		title: "Regional Care Manager",
		copy:
			"Oversees care quality, reviews changing needs and gives families and carers an experienced point of support.",
	},
];

const developmentAreas = [
	[
		"A strong start",
		"Moving and handling, medication, safeguarding, infection control and the judgement that supports safe care at home.",
	],
	[
		"Learning that continues",
		"Further development and condition-specific learning help carers grow with the people and situations they support.",
	],
	[
		"Quality in the everyday",
		"Supervision, care-plan reviews and regular contact mean good care is noticed, supported and improved over time.",
	],
];

const regionalTeamMembers = [
	{
		name: "Olivia",
		image: "olivia.png",
		width: "211",
		height: "244",
	},
	{
		name: "Sarah",
		image: "team/sarah.png",
		width: "873",
		height: "1044",
	},
	{
		name: "Orianne",
		image: "team/orianne.png",
		width: "897",
		height: "1049",
	},
];

export default function OurCarers() {
	const [activeTechnologyView, setActiveTechnologyView] = useState(0);
	const selectedTechnologyView = technologyViews[activeTechnologyView];

	return (
		<div className="container edyn-redesign-shell">
			<Seo
				title="Professional Live-in Carers | Edyn Care"
				description="Meet the professional carers behind Edyn's live-in care service and learn how we select, train and support them with a dedicated regional care team."
				path="/our-carers/"
			/>
			<Header />

			<main id="main-content" className="edyn-carers">
				<section className="carers-hero">
					<div className="site-shell carers-hero__panel">
						<div className="carers-hero__copy">
							<p className="eyebrow eyebrow--light">Our professional carers</p>
							<h1>The right carer changes how home feels.</h1>
							<p className="carers-hero__lead">
								We choose experienced live-in carers for the care they can
								provide and the kind of person they are: calm, capable and able
								to make everyday life feel more like itself.
							</p>
							<ul>
								<li>Only 2% of applicants are accepted and trained by Edyn</li>
								<li>Carefully checked, trained and supported professionals</li>
								<li>A regional care team stays involved throughout care</li>
							</ul>
							<div className="button-row">
								<Link className="button button--cream" to="/care-consultation/">
									Talk to a Care Advisor
								</Link>
								<a className="button button--text-light" href="#how-we-select">
									How we select carers <span aria-hidden="true">&#8595;</span>
								</a>
							</div>
							<div className="carers-hero__trustpilot">
								<TrustpilotInline />
							</div>
						</div>

						<div className="carers-hero__visual">
							<img
								src={`${imagePath}/oc-header.png`}
								alt="A professional carer sharing a moment with an older person at home"
								width="3053"
								height="2297"
							/>
							<div className="carers-hero__note">
								<span>2%</span>
								<p>
									<strong>Accepted and trained</strong>
									from carer applications
								</p>
							</div>
						</div>
					</div>
				</section>

				<section
					className="carers-quality section-space"
					aria-labelledby="quality-title"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">The standards behind every placement</p>
								<h2 id="quality-title">The reassurance should be specific.</h2>
							</div>
							<p>
								The right carer needs more than a kind manner. Before they join
								a home, we check the details, prepare them carefully and make
								sure they are supported to do good work every day.
							</p>
						</div>

						<div className="carers-quality__grid">
							{qualityStandards.map((standard) => (
								<article key={standard.number}>
									<span>{standard.number}</span>
									<h3>{standard.title}</h3>
									<ul>
										{standard.items.map((item) => (
											<li key={item}>{item}</li>
										))}
									</ul>
								</article>
							))}
						</div>
						<div className="carers-quality__trustpilot">
							<p>
								Families can also read independent feedback from people who have
								arranged care with Edyn.
							</p>
							<a
								className="carers-quality__rating"
								href="https://uk.trustpilot.com/review/edyn.care"
								target="_blank"
								rel="noopener"
								aria-label="Excellent: five out of five stars on Trustpilot"
							>
								<strong>Excellent</strong>
								<span aria-hidden="true">
									{Array.from({ length: 5 }, (_, index) => (
										<i key={index}>&#9733;</i>
									))}
								</span>
							</a>
						</div>
					</div>
				</section>

				<section className="carers-person section-space">
					<div className="site-shell carers-person__grid">
						<div className="carers-person__copy">
							<p className="eyebrow">More than a list of tasks</p>
							<h2>The skills matter. The person matters just as much.</h2>
							<p>
								A live-in carer becomes part of the rhythm of home: noticing
								what is needed, protecting privacy and making room for the
								things that make each day feel familiar.
							</p>
							<p>
								That is why we match around practical experience and personal
								fit, then keep listening as the relationship and care plan
								develop.
							</p>
							<ul>
								<li>Personal care delivered with dignity</li>
								<li>Help with medication, mobility and daily routines</li>
								<li>Companionship, meals and the small details of home life</li>
							</ul>
						</div>
						<figure className="carers-person__image">
						<picture>
							<source srcSet={`${imagePath}/carer-helping-with-haircut-800.webp 800w, ${imagePath}/carer-helping-with-haircut-1600.webp 1600w`} sizes="(max-width: 700px) 100vw, 50vw" type="image/webp" />
							<img src={`${imagePath}/carer-helping-with-haircut-800.webp`} alt="A professional carer helping a gentleman with his haircut outdoors" width="800" height="731" loading="lazy" />
						</picture>
							<figcaption>
								<span aria-hidden="true">&#10003;</span>
								<p>
									<strong>Care that notices the whole person.</strong>
									Not just the care plan.
								</p>
							</figcaption>
						</figure>
					</div>
				</section>
				<section
					className="carers-profiles section-space"
					aria-labelledby="profiles-title"
				>
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow eyebrow--light">
									Meet the people behind the care
								</p>
								<h2 id="profiles-title">
									A great match is about more than qualifications.
								</h2>
							</div>
							<p>
								Each proposed carer comes with a clear profile, so a family can
								understand their experience, skills and the small strengths that
								make them right for a particular home.
							</p>
						</div>

						<div className="carers-profiles__grid">
							{carerProfiles.map((profile) => (
								<article key={profile.name}>
									<div className="carers-profiles__person">
										<img
											src={`${imagePath}/${profile.image}`}
											alt={`${profile.name}, an Edyn professional carer`}
											width="225"
											height="294"
											loading="lazy"
										/>
										<div>
											<h3>{profile.name}</h3>
											<p className="carers-profiles__experience">
												<ProfileIcon type="experience" />
												{profile.years} years' experience
											</p>
										</div>
									</div>
									<div
										className="carers-profiles__travel"
										aria-label="Travel details"
									>
										{profile.travel.map((item) => (
											<span key={item}>
												<ProfileIcon type="driver" />
												{item}
											</span>
										))}
									</div>
									<dl>
										<div>
											<dt>
												<ProfileIcon type="language" />
												Languages
											</dt>
											<dd>{profile.languages}</dd>
										</div>
										<div>
											<dt>
												<ProfileIcon type="conditions" />
												Experienced in
											</dt>
											<dd className="carers-profiles__pills">
												{profile.conditions.map((condition) => (
													<span key={condition}>{condition}</span>
												))}
											</dd>
										</div>
										<div>
											<dt>
												<ProfileIcon type="skills" />
												Key skills
											</dt>
											<dd className="carers-profiles__pills">
												{profile.skills.map((skill) => (
													<span key={skill}>{skill}</span>
												))}
											</dd>
										</div>
										<div className="carers-profiles__superpower">
											<dt>Superpower</dt>
											<dd>{profile.superpower}</dd>
										</div>
									</dl>
								</article>
							))}
						</div>
					</div>
				</section>

				<section id="how-we-select" className="carers-selection">
					<div className="site-shell">
						<div className="section-heading section-heading--split">
							<div>
								<p className="eyebrow">How we select carers</p>
								<h2>Chosen deliberately. Supported properly.</h2>
							</div>
							<p>
								One carer lives in the home, but they arrive through a
								considered process led by people who understand professional
								care.
							</p>
						</div>

						<div className="carers-selection__grid">
							<article>
								<span>01</span>
								<h3>Professional experience</h3>
								<p>
									Every Edyn carer brings at least 12 months of hands-on
									professional care experience before they join us.
								</p>
							</article>
							<article>
								<span>02</span>
								<h3>Scenario testing</h3>
								<p>
									We look at how people communicate, make judgements and respond
									to the real situations that arise in a home.
								</p>
							</article>
							<article>
								<span>03</span>
								<h3>One-to-one interview</h3>
								<p>
									A dedicated conversation helps us understand experience,
									values, professional judgement and personal goals.
								</p>
							</article>
							<article>
								<span>04</span>
								<h3>Induction and support</h3>
								<p>
									Training is followed by regular contact, development and
									quality overview from the wider Edyn team.
								</p>
							</article>
						</div>
					</div>
				</section>

				<section className="carers-review-strip" aria-label="Customer reviews">
					<div className="site-shell">
						<TrustpilotInline />
					</div>
				</section>

				<section
					className="carers-technology section-space"
					aria-labelledby="technology-title"
				>
					<div className="site-shell carers-technology__grid">
						<div className="carers-technology__copy">
							<p className="eyebrow">Care that stays connected</p>
							<h2 id="technology-title">
								Technology that helps people care well.
							</h2>
							<p>
								Edyn technology gives carers and the regional care team useful,
								up-to-date information about what is happening in the home. It
								makes quality oversight clearer, while the relationship stays
								human.
							</p>
							<div
								className="carers-technology__views"
								aria-label="Care app views"
							>
								{technologyViews.map((view, index) => {
									const Icon = view.icon;

									return (
										<button
											key={view.title}
											type="button"
											className={
												index === activeTechnologyView ? "is-active" : ""
											}
											aria-pressed={index === activeTechnologyView}
											onClick={() => setActiveTechnologyView(index)}
										>
											<Icon aria-hidden="true" />
											<span>{view.title}</span>
											<span aria-hidden="true">&#8594;</span>
										</button>
									);
								})}
							</div>
						</div>
						<figure className="carers-technology__visual">
							<img
								src={`${imagePath}/${selectedTechnologyView.image}`}
								alt={selectedTechnologyView.alt}
								width="1782"
								height="1365"
							/>
							<figcaption aria-live="polite">
								<strong>{selectedTechnologyView.title}</strong>
								{selectedTechnologyView.copy}
							</figcaption>
						</figure>
					</div>
				</section>

				<section className="carers-team section-space">
					<div className="site-shell">
						<div className="carers-team__heading">
							<div>
								<p className="eyebrow eyebrow--light">
									Managed care, every day
								</p>
								<h2>
									One carer in the home. A regional team staying with them.
								</h2>
								<p>
									A successful placement is never left to one person alone. The
									carer is supported by a local team who coordinate the detail
									and stay close as needs change.
								</p>
							</div>
							<div
								className="carers-team__portraits"
								role="group"
								aria-label="Olivia, Sarah and Orianne from the Edyn regional care team"
							>
								{regionalTeamMembers.map((member) => (
									<span key={member.name}>
										<img
											src={`${imagePath}/${member.image}`}
											alt={`${member.name}, a member of the Edyn regional care team`}
											width={member.width}
											height={member.height}
											loading="lazy"
										/>
									</span>
								))}
							</div>
						</div>

						<div className="carers-team__roles">
							{careTeamRoles.map((role) => (
								<article key={role.number}>
									<span>{role.number}</span>
									<h3>{role.title}</h3>
									<p>{role.copy}</p>
								</article>
							))}
						</div>
					</div>
				</section>

				<section className="carers-development section-space">
					<div className="site-shell carers-development__grid">
						<div className="carers-development__intro">
							<p className="eyebrow">Quality that continues</p>
							<h2>Training does not end on the first day.</h2>
							<p>
								Care changes as people change. We invest in learning, mentoring
								and quality support so carers can keep doing thoughtful, capable
								work.
							</p>
							<Link className="text-link" to="/become-a-carer/">
								Explore carer opportunities{" "}
								<span aria-hidden="true">&#8594;</span>
							</Link>
						</div>
						<div className="carers-development__list">
							{developmentAreas.map(([title, copy], index) => (
								<article key={title}>
									<span>{`0${index + 1}`}</span>
									<div>
										<h3>{title}</h3>
										<p>{copy}</p>
									</div>
								</article>
							))}
						</div>
					</div>
				</section>
			</main>
			<Footer includeBecomeACarer />
		</div>
	);
}
